/**
 * 空画布引导层（DESIGN.md §7.1 补充）。
 *
 * 没有文档时，画布区只剩一块棋盘格占位 + 状态栏一行很容易被忽略的
 * "尚未打开文档"小字——不熟悉的人打开页面不知道下一步该干什么。
 * 参照真实 Photoshop 的启动欢迎屏：叠一层引导，给两个大按钮直达
 * 「新建」「打开」，不需要先摸到菜单在哪。
 *
 * 只负责展示与转发点击，不知道"新建"/"打开"具体怎么做——那是装配层
 * （main.tsx）接到 ActionRegistry 上的事，这里不 import 任何下层模块
 * （CLAUDE.md 规则 4）。
 */
import type { JSX } from 'solid-js';
import { For } from 'solid-js';
import styles from './EmptyCanvasOverlay.module.css';

export interface EmptyCanvasOverlayProps {
  readonly onNewDocument: () => void;
  readonly onOpenFile: () => void;
  /** 点某个场景卡片：直达该类目预选的「新建文档」对话框。 */
  readonly onNewFromCategory: (categoryId: string) => void;
  /** 点「批量处理图片」卡片。 */
  readonly onOpenBatch: () => void;
}

/**
 * 只是普通字符串约定（跟 `app/document-templates.ts` 的类目 id 对应），
 * 不 import 那个模块的类型——纯展示组件不该知道模板数据长什么样
 * （CLAUDE.md 规则 4）。
 */
const SCENARIO_CARDS: ReadonlyArray<{ readonly categoryId: string; readonly label: string }> = [
  { categoryId: 'social', label: '社交媒体图片' },
  { categoryId: 'ecommerce', label: '电商主图' },
  { categoryId: 'app', label: 'App 截图' },
  { categoryId: 'website', label: '网站素材' },
];

export function EmptyCanvasOverlay(props: EmptyCanvasOverlayProps): JSX.Element {
  return (
    <div class={styles.overlay} data-testid="empty-canvas-overlay">
      <div class={styles.card}>
        <p class={styles.hint}>开始你的第一个项目</p>
        <div class={styles.actions}>
          <button
            type="button"
            class={styles.primary}
            data-testid="empty-canvas-new"
            onClick={props.onNewDocument}
          >
            新建文档
          </button>
          <button
            type="button"
            class={styles.secondary}
            data-testid="empty-canvas-open"
            onClick={props.onOpenFile}
          >
            打开文件
          </button>
        </div>
        <div class={styles.scenarioGrid}>
          <For each={SCENARIO_CARDS}>
            {(card) => (
              <button
                type="button"
                class={styles.scenarioCard}
                data-testid={`empty-canvas-scenario-${card.categoryId}`}
                onClick={() => props.onNewFromCategory(card.categoryId)}
              >
                {card.label}
              </button>
            )}
          </For>
          <button
            type="button"
            class={styles.scenarioCard}
            data-testid="empty-canvas-batch"
            onClick={props.onOpenBatch}
          >
            批量处理图片
          </button>
        </div>
      </div>
    </div>
  );
}
